import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, ArrowRight, TrendingUp, Shield, Activity, BarChart3, Users, Mail, Phone, Twitter, ChevronRight, CheckCircle2, Zap, Target, Brain, Linkedin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/purpose", label: "Purpose" },
    { to: "/services", label: "Services" },
    { to: "/performance", label: "Performance" },
    { to: "/reviews", label: "Reviews" },
    { to: "/contact", label: "Contact" }
  ];

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-emerald-500/10' : 'bg-transparent'}`}>
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_systematic-trade/artifacts/wlb4m1ky_Logo_Portrait%20-%20Copy%281%29.png" 
              alt="Theta Capitals Logo" 
              className="h-10 w-auto"
            />
            <span className="text-white font-bold text-xl">Theta Capitals</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-emerald-400 ${
                  location.pathname === link.to ? 'text-emerald-400' : 'text-gray-300'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white"
            data-testid="mobile-menu-button"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className={`block text-sm font-medium transition-colors hover:text-emerald-400 ${
                  location.pathname === link.to ? 'text-emerald-400' : 'text-gray-300'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-black border-t border-emerald-500/10 py-12">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Theta Capitals - Left */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_systematic-trade/artifacts/wlb4m1ky_Logo_Portrait%20-%20Copy%281%29.png" 
                alt="Theta Capitals Logo" 
                className="h-8 w-auto"
              />
              <span className="text-white font-bold">Theta Capitals</span>
            </div>
            <p className="text-gray-400 text-sm">Algorithmic Trading | Wealth Creation</p>
          </div>

          {/* Quick Links - Center */}
          <div className="md:text-center">
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <div className="space-y-2">
              <Link to="/purpose" className="block text-gray-400 hover:text-emerald-400 text-sm transition-colors" data-testid="footer-purpose">Purpose</Link>
              <Link to="/services" className="block text-gray-400 hover:text-emerald-400 text-sm transition-colors" data-testid="footer-services">Services</Link>
              <Link to="/performance" className="block text-gray-400 hover:text-emerald-400 text-sm transition-colors" data-testid="footer-performance">Performance</Link>
              <Link to="/reviews" className="block text-gray-400 hover:text-emerald-400 text-sm transition-colors" data-testid="footer-reviews">Reviews</Link>
              <Link to="/contact" className="block text-gray-400 hover:text-emerald-400 text-sm transition-colors" data-testid="footer-contact">Contact</Link>
            </div>
          </div>

          {/* Contact - Right */}
          <div className="md:text-right">
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <div className="space-y-2 text-sm text-gray-400">
              <a href="mailto:thetacapitals@gmail.com" className="flex items-center space-x-2 hover:text-emerald-400 transition-colors md:justify-end" data-testid="footer-email">
                <Mail size={16} />
                <span>thetacapitals@gmail.com</span>
              </a>
              <a href="tel:+918109273602" className="flex items-center space-x-2 hover:text-emerald-400 transition-colors md:justify-end" data-testid="footer-phone">
                <Phone size={16} />
                <span>+91 81092 73602</span>
              </a>
              <a href="https://twitter.com/thetacapitals" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 hover:text-emerald-400 transition-colors md:justify-end" data-testid="footer-twitter">
                <Twitter size={16} />
                <span>@thetacapitals</span>
              </a>
              <a href="https://www.linkedin.com/company/theta-capitals" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 hover:text-emerald-400 transition-colors md:justify-end" data-testid="footer-linkedin">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span>Theta Capitals</span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-emerald-500/10 pt-8 text-center text-gray-400 text-sm">
          <p>© 2026 Theta Capitals. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/918109273602"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-button"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-600 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all"
      aria-label="Chat on WhatsApp"
    >
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
    </a>
  );
};

// Home Page
const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1640340434855-6084b1f4901c?q=80&w=2064" 
            alt="Trading Background" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black"></div>
        </div>
        
        <div className="absolute inset-0 grid-background"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black"></div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <div className="fade-in-up">
            <div className="inline-block mb-4 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
              <span className="text-emerald-400 text-sm font-medium">Engineered for consistent alpha</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Fully Automated<br />
              <span className="text-gradient">Algorithmic Trading</span><br />
              in Indian Markets
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              Zero emotion. Pure mathematics. Consistent alpha.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://wa.me/918109273602"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-schedule-call-button"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-lg transition-all hover:scale-105 flex items-center space-x-2 shadow-lg shadow-emerald-500/20"
              >
                <span>Let Our Algorithms Handle Your Trading Account for Consistent Gains</span>
                <ChevronRight size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-emerald-500/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-emerald-500 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* What Theta Capitals Does */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden">
        {/* Background decorative element */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4" data-testid="what-theta-heading">
            What <span className="text-gradient">Theta Capitals</span> Does
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Full-spectrum algorithmic trading powered by cutting-edge technology and proven strategies
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Zap size={32} />,
                title: "Fully Automated 24×7",
                description: "Full-time algo trading with zero manual intervention. From 9:15 AM to commodity night sessions — your capital is managed by our system. No discretionary tweaks, no second-guessing, just disciplined execution round the clock.",
                testId: "feature-automated"
              },
              {
                icon: <Target size={32} />,
                title: "Custom Backtesting Engine",
                description: "Built entirely in-house using premium minute data from NSE, BSE & MCX. No approximations, no third-party shortcuts — simulations that mirror live-market slippage, costs, and execution reality.",
                testId: "feature-backtested"
              },
              {
                icon: <Shield size={32} />,
                title: "Strict Risk Control",
                description: "No exceptions: daily risk capped at 1%, overall drawdown never exceeds 3%. Engineered for capital preservation while delivering reliable 3–4% monthly compounding.",
                testId: "feature-risk"
              },
              {
                icon: <Activity size={32} />,
                title: "Efficient Capital Use",
                description: "Leverage your existing Shares and Mutual fund units as collateral. Run full-size intraday strategies on NSE, BSE & MCX with minimal cash — up to 90% funded via pledge margins.",
                testId: "feature-transparent"
              }
            ].map((feature, index) => (
              <div key={index} className="feature-card p-6 rounded-xl bg-gray-800/50 border border-emerald-500/10 hover:border-emerald-500/30 transition-all" data-testid={feature.testId}>
                <div className="text-emerald-400 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Theta Capitals */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12" data-testid="why-theta-heading">
            Why <span className="text-gradient">Theta Capitals</span>
          </h2>
          
          <div className="max-w-5xl mx-auto">
            {/* Hero Image Banner */}
            <div className="mb-12 rounded-xl overflow-hidden relative image-overlay">
              <img 
                src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=2070" 
                alt="Trading Dashboard" 
                className="w-full h-64 md:h-80 object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-6">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Automated Trading Excellence</h3>
                  <p className="text-emerald-400 text-lg">Real-time execution across NSE, BSE & MCX</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8 md:p-10 neon-glow">
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                At Theta Capitals, we are <span className="text-emerald-400 font-semibold">full-time derivatives algo traders</span> running fully automated, emotion-free strategies across NSE, BSE, and MCX. Every rupee works harder — we leverage pledged securities and margins from existing holdings so your capital is never sitting idle.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Our strategies are built for consistency: targeting <span className="text-emerald-400 font-semibold">3–4% average monthly returns</span> with a hard <span className="text-emerald-400 font-semibold">1% daily loss cap</span> and maximum drawdown under <span className="text-emerald-400 font-semibold">3%</span>. No curve-fitted dreams — every system is validated on our in-house backtesting engine using premium minute data that mirrors real slippage, costs, and exchange behaviour.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Your trust is everything to us. That's why every client gets <span className="text-emerald-400 font-semibold">direct, one-on-one access to the founders</span> for strategy discussions, financial planning sessions, and instant support on any question — big or small. We succeed only when you do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Live Strategies */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4" data-testid="strategies-heading">
            Live <span className="text-gradient">Strategy Performance</span>
          </h2>
          <p className="text-gray-400 text-center mb-12">Real-time performance metrics updated daily</p>
          
          <div className="max-w-6xl mx-auto">
            <div className="strategy-card bg-black border border-emerald-500/20 rounded-xl p-6 md:p-8" data-testid="strategy-performance-sheet">
              <div className="mb-6 text-center">
                <p className="text-gray-400 text-sm">
                  Live performance data synced from our trading systems
                </p>
              </div>
              
              {/* Google Sheets Embed 
                  
                  HOW TO UPDATE WITH YOUR GOOGLE SHEET:
                  1. Open your Google Sheet
                  2. Click File > Share > Publish to web
                  3. Choose "Embed" and click "Publish"
                  4. Copy the iframe src URL
                  5. Replace the src URL below with your published sheet URL
                  
                  Example format: 
                  https://docs.google.com/spreadsheets/d/e/YOUR_SHEET_ID/pubhtml?widget=true&headers=false
              */}
              <div className="google-sheets-container bg-gray-900 rounded-lg overflow-hidden" style={{minHeight: '600px'}}>
                <iframe 
                  src="https://docs.google.com/spreadsheets/d/10KcUBM5KA47Ak3pv6YT9OWR1Ich3u3ptQ7EhiVJAwoE/preview?widget=true&headers=false"
                  className="w-full h-full"
                  style={{minHeight: '600px', border: 'none'}}
                  title="Strategy Performance Data"
                  data-testid="google-sheets-embed"
                />
              </div>
              
              <div className="mt-6 text-center">
                {/* Instruction text removed */}
              </div>
            </div>
            
            <div className="mt-8 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-6 text-center">
              <p className="text-gray-300 mb-4">
                Want detailed strategy breakdown and historical performance?
              </p>
              <Link
                to="/performance"
                data-testid="view-detailed-performance"
                className="inline-flex items-center space-x-2 text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
              >
                <span>View Detailed Performance Report</span>
                <ChevronRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-5xl font-bold text-white mb-6" data-testid="final-cta-heading">
              Ready to earn additional income<br />from your holdings using our strategies?
            </h2>
            <p className="text-gray-400 text-lg mb-10">Join sophisticated investors leveraging automated trading strategies for consistent returns</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/918109273602"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-cta-schedule-call"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-lg transition-all hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Contact on WhatsApp</span>
                <ChevronRight size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Purpose Page (formerly About)
const Purpose = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="about-page pt-24">
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Hero Image - Theta Capitals Logo */}
            <div className="mb-12 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_systematic-trade/artifacts/vmhulvi9_Logo_Landscape.png" 
                alt="Theta Capitals" 
                className="w-full max-w-2xl h-auto"
              />
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" data-testid="about-heading">
              Our <span className="text-gradient">Purpose</span>
            </h1>
            <p className="text-xl text-gray-300 mb-12">
              We're systematic traders obsessed with one question: how do you extract consistent alpha from Indian markets without taking stupid risks? The answer is disciplined code, relentless backtesting, and zero emotional interference.
            </p>

            <div className="space-y-8">
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center" data-testid="mission-heading">
                  <Brain className="mr-3 text-emerald-400" size={28} />
                  Our Mission
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  To become the most trusted name in systematic derivatives trading for Indian investors — by being radically transparent, obsessively risk-averse, and always available when you have a question.
                </p>
              </div>

              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center" data-testid="approach-heading">
                  <TrendingUp className="mr-3 text-emerald-400" size={28} />
                  Our Approach
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Every strategy we run follows the same non-negotiable principles:
                </p>
                <ul className="space-y-3">
                  {[
                    "Built and validated in-house using premium minute data across NSE, BSE & MCX",
                    "Minimum 6 months of live forward-testing + personal capital deployment before client onboarding",
                    "Hard 1% daily loss cap and maximum drawdown never above 3%",
                    "Full automation — zero manual overrides, 24×7 monitoring from 9:15 AM to 11:30 PM (including commodity night sessions)",
                    "Capital-efficient execution — leverage pledged shares & MF units so your long-term investments keep generating extra income through intraday & positional algo trades",
                    "Obsessed with consistency — we target steady 3–4% monthly compounding, not get-rich-quick lottery tickets",
                    "Statistical edge proven at 95%+ confidence with full transparency on slippage and costs",
                    "Direct founder access — one-on-one financial planning sessions, instant query resolution, and complete alignment: we only make money when you do"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start" data-testid={`approach-item-${index}`}>
                      <CheckCircle2 className="mr-3 text-emerald-400 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center" data-testid="values-heading">
                  <Shield className="mr-3 text-emerald-400" size={28} />
                  Our Values
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { title: "Transparency", desc: "Full disclosure of methodologies and performance metrics" },
                    { title: "Risk Management", desc: "Capital preservation is our top priority" },
                    { title: "Innovation", desc: "Continuously researching and developing new strategies" },
                    { title: "Client-First", desc: "Your success is our success" }
                  ].map((value, index) => (
                    <div key={index} data-testid={`value-${index}`}>
                      <h3 className="text-lg font-bold text-emerald-400 mb-2">{value.title}</h3>
                      <p className="text-gray-400 text-sm">{value.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA removed as requested */}
          </div>
        </div>
      </section>
    </div>
  );
};

// Services Page
const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      title: "Algo Trading Strategies",
      icon: <BarChart3 size={40} />,
      description: "",
      features: [
        "Fully automated intraday options trading strategies on NSE, BSE & MCX",
        "Momentum and mean-reversion systems",
        "Multi-timeframe execution with smart order routing",
        "Zero manual intervention — runs 9:15 AM to 11:30 PM"
      ],
      testId: "service-algo-trading"
    },
    {
      title: "Dual Income Generation (by Pledging)",
      icon: <Target size={40} />,
      description: "",
      features: [
        "Your long-term shares & MF units work twice — earn dividends + generate daily trading income",
        "Up to 90% margin funding via pledge (Zerodha, Upstox, Angel One, etc.)",
        "Run ₹50L–₹5Cr strategies with as little as 5% cash",
        "No need to sell blue-chips — keep compounding while algo trades on the margin",
        "Legally compliant, exchange-approved pledging with zero lock-in pressure"
      ],
      testId: "service-dual-income"
    },
    {
      title: "Research and Backtesting",
      icon: <Brain size={40} />,
      description: "",
      features: [
        "100% in-house research using Python, Pandas, NumPy",
        "Premium minute-by-minute data (premium data)",
        "We follow proper process driven approach before deploying any strategy.",
        "Idea generation → Backtesting → Walk forward testing → Monte-Carlo Analysis → Forward testing → Live Vs Backtest Comparison → Scaling up",
        "We use our self developed backtest engine as our strategies can't be backtested on third party backtest platforms and also they are not reliable enough."
      ],
      testId: "service-research"
    },
    {
      title: "Systematic Trading",
      icon: <Shield size={40} />,
      description: "",
      features: [
        "Every rupee, every trade follows strict predefined rules — no discretion, no emotions",
        "Hard 1% daily loss capped",
        "Maximum drawdown engineered below 3% across 3+ years of data",
        "Consistent 3–4% monthly compounding instead of lottery-ticket returns",
        "Same strategies running live on founders' personal capital"
      ],
      testId: "service-systematic"
    },
    {
      title: "Guidance on Investments & Personal Finance",
      icon: <Users size={40} />,
      description: "",
      features: [
        "One-on-one financial planning sessions with the founders",
        "Custom capital allocation based on your goals, risk appetite & tax situation",
        "Help with goal-based investing and insurance planning",
        "Portfolio reviews and rebalancing",
        "Lifetime direct WhatsApp access — ask anything, anytime"
      ],
      testId: "service-guidance"
    },
    {
      title: "Trading Reports – Live Results vs Backtest",
      icon: <Activity size={40} />,
      description: "",
      features: [
        "Weekly & monthly performance reports",
        "Side-by-side live vs backtest comparison (including slippage)",
        "P&L, drawdown tracker, win rate, etc",
        "Monthly review explaining performance deviations and improvements"
      ],
      testId: "service-reports"
    }
  ];

  return (
    <div className="services-page pt-24">
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto mb-16 text-center">
            {/* Services Hero Image */}
            <div className="mb-12 rounded-xl overflow-hidden relative image-overlay">
              <img 
                src="https://images.unsplash.com/photo-1642790106117-e829e14a795f?q=80&w=2070" 
                alt="Professional Trading Services" 
                className="w-full h-64 object-cover opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="services-page-heading">
                  Our <span className="text-gradient">Services</span>
                </h1>
              </div>
            </div>
            <p className="text-xl text-gray-300">
              Comprehensive algorithmic trading solutions designed for the sophisticated investor
            </p>
          </div>

          {/* Top Two Service Boxes */}
          <div className="max-w-5xl mx-auto mb-16 space-y-8">
            {/* Account Handling Services Box */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-8" data-testid="account-handling-service">
              <div className="text-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                  Account Handling Services
                </h2>
                <p className="text-emerald-400 text-xl font-semibold mb-2">Fully Automated Trading on Your Account</p>
              </div>
              <div className="max-w-3xl mx-auto">
                <p className="text-gray-300 mb-4 text-center">
                  Let us manage your trading account with our proven algorithmic strategies:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {[
                    "Intraday Options Trading on NSE, BSE & MCX",
                    "Trading Margin from Pledged Investments (Shares/MFs)",
                    "Target: 3-4% Returns Per Month",
                    "Max Loss Per Day: Capped at 1%",
                    "Maximum Drawdown: Less than 3%",
                    "Fully Automated System - No Manual Intervention"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start" data-testid={`account-handling-item-${index}`}>
                      <CheckCircle2 className="mr-2 text-emerald-400 flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <a
                    href="https://wa.me/918109273602?text=Hi,%20I%20would%20like%20to%20know%20more%20about%20the%20account%20handling%20services"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="account-handling-whatsapp-button"
                    className="inline-flex items-center space-x-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-lg transition-all hover:scale-105"
                  >
                    <span>Contact on WhatsApp</span>
                    <ArrowRight size={20} />
                  </a>
                </div>
              </div>
            </div>

            {/* Personal Finance Advisory Session Box */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-8" data-testid="personal-finance-session">
              <div className="text-center mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                  One on One Personal Finance Advisory Session
                </h2>
                <p className="text-emerald-400 text-xl font-semibold mb-2">30 Minutes with the Founder | Just ₹500</p>
              </div>
              <div className="max-w-3xl mx-auto">
                <p className="text-gray-300 mb-4 text-center">
                  Get personalized financial guidance tailored to your goals and situation. This session covers:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {[
                    "Existing Investments or Policies Review",
                    "Medical and Term Insurance Planning",
                    "Portfolio Creation & Optimization",
                    "Goal-based Asset Allocation",
                    "Investment Strategy & Where to Invest",
                    "Risk Assessment & Financial Planning"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start" data-testid={`session-item-${index}`}>
                      <CheckCircle2 className="mr-2 text-emerald-400 flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <a
                    href="https://wa.me/918109273602?text=Hi,%20I%20would%20like%20to%20book%20a%2030-minute%20Personal%20Finance%20Advisory%20Session"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="book-finance-session-button"
                    className="inline-flex items-center space-x-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-lg transition-all hover:scale-105"
                  >
                    <span>Book Your Session Now</span>
                    <ArrowRight size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Remaining 6 Service Boxes */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8 hover:border-emerald-500/30 transition-all" data-testid={service.testId}>
                <div className="flex items-start space-x-4 mb-6">
                  <div className="text-emerald-400 flex-shrink-0">{service.icon}</div>
                  <h2 className="text-2xl font-bold text-white">{service.title}</h2>
                </div>
                <div className="space-y-3">
                  {service.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-start" data-testid={`${service.testId}-feature-${fIndex}`}>
                      <CheckCircle2 className="mr-3 text-emerald-400 flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Performance Page
const Performance = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="performance-page pt-24">
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            {/* Performance Hero Image */}
            <div className="mb-12 rounded-xl overflow-hidden relative image-overlay">
              <img 
                src="https://images.unsplash.com/photo-1642543492481-44e81e3914a7?q=80&w=2070" 
                alt="Performance Analytics" 
                className="w-full h-72 object-cover opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="performance-page-heading">
                    Performance <span className="text-gradient">Track Record</span>
                  </h1>
                  <p className="text-emerald-400 text-lg">Transparent Results | Real Data</p>
                </div>
              </div>
            </div>

            {/* Historical Returns Chart */}
            <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-white mb-6" data-testid="historical-returns-heading">Historical Returns</h2>
              <div className="google-sheets-container bg-gray-900 rounded-lg overflow-hidden" style={{minHeight: '600px'}}>
                <iframe 
                  src="https://docs.google.com/spreadsheets/d/1JY2205-ZhbSdJkDU9_xNo4sFzqdbNJ5_/preview?widget=true&headers=false"
                  className="w-full h-full"
                  style={{minHeight: '600px', border: 'none'}}
                  title="Historical Returns Data"
                  data-testid="historical-returns-chart"
                />
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-12 bg-gray-900 border border-emerald-500/10 rounded-xl p-6">
              <p className="text-gray-400 text-sm leading-relaxed" data-testid="performance-disclaimer">
                <strong className="text-white">Disclaimer:</strong> Past performance is not indicative of future results. Trading involves risk, you would be responsible for any profit or losses. Individual results may vary based on market conditions, capital allocation, and timing of entry.
              </p>
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/contact"
                data-testid="performance-cta-button"
                className="inline-flex items-center space-x-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-lg transition-all hover:scale-105"
              >
                <span>Start Your Journey</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data) {
        setSubmitStatus({ type: "success", message: "Thank you! We'll get back to you within 24 hours." });
        setFormData({ name: "", email: "", phone: "", message: "" });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus({ type: "error", message: "Something went wrong. Please try again or contact us directly." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page pt-24">
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            {/* Contact Hero Image */}
            <div className="mb-12 rounded-xl overflow-hidden relative image-overlay">
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015" 
                alt="Get in Touch" 
                className="w-full h-64 object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="contact-page-heading">
                    Get in <span className="text-gradient">Touch</span>
                  </h1>
                  <p className="text-xl text-gray-300">
                    Ready to transform your trading? Let's talk.
                  </p>
                </div>
              </div>
            </div>

            {/* Top Two Service Boxes */}
            <div className="mb-12 space-y-8">
              {/* Account Handling Services Box */}
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-8" data-testid="account-handling-contact">
                <div className="text-center mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                    Account Handling Services
                  </h2>
                  <p className="text-emerald-400 text-xl font-semibold mb-2">Fully Automated Trading on Your Account</p>
                </div>
                <div className="max-w-3xl mx-auto">
                  <p className="text-gray-300 mb-4 text-center">
                    Let us manage your trading account with our proven algorithmic strategies:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {[
                      "Intraday Options Trading on NSE, BSE & MCX",
                      "Trading Margin from Pledged Investments (Shares/MFs)",
                      "Target: 3-4% Returns Per Month",
                      "Max Loss Per Day: Capped at 1%",
                      "Maximum Drawdown: Less than 3%",
                      "Fully Automated System - No Manual Intervention"
                    ].map((item, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle2 className="mr-2 text-emerald-400 flex-shrink-0 mt-1" size={18} />
                        <span className="text-gray-300 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-center">
                    <a
                      href="https://wa.me/918109273602?text=Hi,%20I%20would%20like%20to%20know%20more%20about%20the%20account%20handling%20services"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-account-handling-button"
                      className="inline-flex items-center space-x-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-lg transition-all hover:scale-105"
                    >
                      <span>Contact on WhatsApp</span>
                      <ArrowRight size={20} />
                    </a>
                  </div>
                </div>
              </div>

              {/* Personal Finance Advisory Session Box */}
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-8" data-testid="finance-session-contact">
                <div className="text-center mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                    One on One Personal Finance Advisory Session
                  </h2>
                  <p className="text-emerald-400 text-xl font-semibold mb-2">30 Minutes with the Founder | Just ₹500</p>
                </div>
                <div className="max-w-3xl mx-auto">
                  <p className="text-gray-300 mb-4 text-center">
                    Get personalized financial guidance tailored to your goals and situation. This session covers:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {[
                      "Existing Investments or Policies Review",
                      "Medical and Term Insurance Planning",
                      "Portfolio Creation & Optimization",
                      "Goal-based Asset Allocation",
                      "Investment Strategy & Where to Invest",
                      "Risk Assessment & Financial Planning"
                    ].map((item, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle2 className="mr-2 text-emerald-400 flex-shrink-0 mt-1" size={18} />
                        <span className="text-gray-300 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-center">
                    <a
                      href="https://wa.me/918109273602?text=Hi,%20I%20would%20like%20to%20book%20a%2030-minute%20Personal%20Finance%20Advisory%20Session"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-finance-session-button"
                      className="inline-flex items-center space-x-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-lg transition-all hover:scale-105"
                    >
                      <span>Book Your Session Now</span>
                      <ArrowRight size={20} />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Details */}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6" data-testid="contact-form-heading">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-gray-300 text-sm font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-form-name"
                      className="w-full px-4 py-3 bg-gray-900 border border-emerald-500/20 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition-colors"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-form-email"
                      className="w-full px-4 py-3 bg-gray-900 border border-emerald-500/20 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-gray-300 text-sm font-medium mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="contact-form-phone"
                      className="w-full px-4 py-3 bg-gray-900 border border-emerald-500/20 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition-colors"
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-gray-300 text-sm font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      data-testid="contact-form-message"
                      className="w-full px-4 py-3 bg-gray-900 border border-emerald-500/20 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                      placeholder="Tell us about your trading goals and how we can help..."
                    ></textarea>
                  </div>

                  {submitStatus && (
                    <div 
                      className={`p-4 rounded-lg ${
                        submitStatus.type === 'success' 
                          ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' 
                          : 'bg-red-500/10 border border-red-500/30 text-red-400'
                      }`}
                      data-testid="contact-form-status"
                    >
                      {submitStatus.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-form-submit"
                    className="w-full px-6 py-4 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-bold rounded-lg transition-all hover:scale-105"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-6">
                <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8">
                  <h2 className="text-2xl font-bold text-white mb-6" data-testid="direct-contact-heading">Direct Contact</h2>
                  <div className="space-y-4">
                    <a 
                      href="mailto:thetacapitals@gmail.com" 
                      className="flex items-start space-x-4 text-gray-300 hover:text-emerald-400 transition-colors group"
                      data-testid="contact-email-link"
                    >
                      <Mail className="text-emerald-400 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <p className="font-medium text-white">Email</p>
                        <p className="text-sm group-hover:text-emerald-400">thetacapitals@gmail.com</p>
                      </div>
                    </a>

                    <a 
                      href="tel:+918109273602" 
                      className="flex items-start space-x-4 text-gray-300 hover:text-emerald-400 transition-colors group"
                      data-testid="contact-phone-link"
                    >
                      <Phone className="text-emerald-400 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <p className="font-medium text-white">Phone</p>
                        <p className="text-sm group-hover:text-emerald-400">+91 81092 73602</p>
                      </div>
                    </a>

                    <a 
                      href="https://twitter.com/thetacapitals" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-start space-x-4 text-gray-300 hover:text-emerald-400 transition-colors group"
                      data-testid="contact-twitter-link"
                    >
                      <Twitter className="text-emerald-400 flex-shrink-0 mt-1" size={24} />
                      <div>
                        <p className="font-medium text-white">Twitter</p>
                        <p className="text-sm group-hover:text-emerald-400">@thetacapitals</p>
                      </div>
                    </a>

                    <a 
                      href="https://www.linkedin.com/company/theta-capitals" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-start space-x-4 text-gray-300 hover:text-emerald-400 transition-colors group"
                      data-testid="contact-linkedin-link"
                    >
                      <svg className="text-emerald-400 flex-shrink-0 mt-1" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      <div>
                        <p className="font-medium text-white">LinkedIn</p>
                        <p className="text-sm group-hover:text-emerald-400">Theta Capitals</p>
                      </div>
                    </a>
                  </div>
                </div>

                <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8">
                  <h2 className="text-2xl font-bold text-white mb-4" data-testid="location-heading">Location</h2>
                  <p className="text-gray-300">India</p>
                  <p className="text-gray-400 text-sm mt-2">Serving clients across India and globally</p>
                </div>

                <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-2" data-testid="whatsapp-cta-heading">Prefer WhatsApp?</h3>
                  <p className="text-gray-300 text-sm mb-4">Get instant responses to your queries</p>
                  <a
                    href="https://wa.me/918109273602"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-button"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-lg transition-all hover:scale-105"
                  >
                    <span>Chat on WhatsApp</span>
                    <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Reviews Page
const Reviews = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="reviews-page pt-24">
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Reviews Hero Image */}
            <div className="mb-12 rounded-xl overflow-hidden relative image-overlay">
              <img 
                src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=2070" 
                alt="Client Reviews" 
                className="w-full h-64 object-cover opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="reviews-page-heading">
                  Client <span className="text-gradient">Reviews</span>
                </h1>
              </div>
            </div>
            
            <p className="text-xl text-gray-300 text-center mb-12">
              What our clients say about their experience with Theta Capitals
            </p>

            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Testimonial 1 - Sagar */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all" data-testid="testimonial-1">
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    "I've been dealing with Theta Capitals for nearly two years, and in an industry where trust is often hard to come by, it's refreshing to find a genuine operation. As a full-time investor, trading on pledged holdings to generate alpha, the consistency of Theta Capitals' trading setups have had a significant impact on my portfolio performance."
                  </p>
                </div>
                <div className="border-t border-emerald-500/10 pt-4">
                  <p className="text-white font-semibold">Sagar</p>
                  <p className="text-gray-500 text-xs">Delhi</p>
                </div>
              </div>

              {/* Testimonial 2 - Hiten */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all" data-testid="testimonial-2">
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    "I have had excellent experience using Theta Capital's algo trading service with well researched, disciplined, low drawdown and completely rule based. The system is transparent, removes emotional trading and works efficiently even in volatile markets. A major advantage for me has been the ability to trade using pledged long term investments, allowing me to generate additional income through algo based trading. The consistency and professional support give complete confidence in the service. I highly recommend Theta Capitals Algo service for reliable solutions."
                  </p>
                </div>
                <div className="border-t border-emerald-500/10 pt-4">
                  <p className="text-white font-semibold">Hiten</p>
                  <p className="text-gray-500 text-xs">Ahmednagar</p>
                </div>
              </div>

              {/* Testimonial 3 - Balaji */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all" data-testid="testimonial-3">
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    "As an investor, I appreciate Theta Capital's thoughtful, hassle-free approach to trading in the Indian market. The automated, algorithm-driven system runs seamlessly in the background, freeing me to focus on my personal work. Theta Capitals has brought confidence and convenience to my trading journey, making investing and trading smoother and more efficient than I expected. Their customer support team is excellent—helpful, patient, and thorough in addressing queries."
                  </p>
                </div>
                <div className="border-t border-emerald-500/10 pt-4">
                  <p className="text-white font-semibold">Balaji</p>
                  <p className="text-gray-500 text-xs">Chennai</p>
                </div>
              </div>

              {/* Testimonial 4 - Deepankar */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all" data-testid="testimonial-4">
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    "Don't think twice if you're planning to take services from Theta Capitals. He is one of the most genuine traders in this industry. Strategies are great with focus on low DD, and he changes them when required to keep up with changing market environment. Trading is done on pledged investment so it's additional income on top of market returns. Plug and play, any office goer can trade without any hassle of watching charts."
                  </p>
                </div>
                <div className="border-t border-emerald-500/10 pt-4">
                  <p className="text-white font-semibold">Deepankar</p>
                  <p className="text-gray-500 text-xs">Almora</p>
                </div>
              </div>

              {/* Testimonial 5 - Swapnil */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all" data-testid="testimonial-5">
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    "I have been associated with Theta Capitals for their algo trading services in indices and commodities, and the experience has been transparent and disciplined. The strategies are well-structured, risk-managed, and completely rule-based, which builds confidence over time. What stands out is their honest communication and realistic return expectations. A reliable platform for anyone looking for systematic trading without unnecessary hype."
                  </p>
                </div>
                <div className="border-t border-emerald-500/10 pt-4">
                  <p className="text-white font-semibold">Swapnil</p>
                  <p className="text-gray-500 text-xs">Ahilyanagar</p>
                </div>
              </div>

              {/* Testimonial 6 - Sree */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all" data-testid="testimonial-6">
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    "I have been utilizing Theta Capitals algo services since late December 2023. Initially, I engaged with their algorithmic baskets, which consistently delivered positive returns. For the past three plus months, I have transitioned to their PMS services, leveraging my pledged holdings for daily MCX trading and index algorithms on expiry days. My returns have consistently demonstrated an upward trajectory. The value investment philosophy has fostered reliable portfolio growth, devoid of significant volatility."
                  </p>
                </div>
                <div className="border-t border-emerald-500/10 pt-4">
                  <p className="text-white font-semibold">Sree</p>
                  <p className="text-gray-500 text-xs">Bengaluru</p>
                </div>
              </div>

              {/* Testimonial 7 - Rudresh */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all" data-testid="testimonial-7">
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    "I met them via an old friend. At first I did not believe their words that they are creating significant profits consistently with options trading. But then after few months I suddenly decided to meet them to get some proofs about their claims. After the meeting I was amazed with the numbers and how they actually cracked the market. Sooner I started my account full fledged and that is one of the best investment decision of my life. Now I am enjoying a good passive income with trust that my money is totally safe with Theta Capitals."
                  </p>
                </div>
                <div className="border-t border-emerald-500/10 pt-4">
                  <p className="text-white font-semibold">Rudresh</p>
                  <p className="text-gray-500 text-xs">Gurgaon</p>
                </div>
              </div>

              {/* Testimonial 8 - Manjunath */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all" data-testid="testimonial-8">
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    "I've had a great experience working with Theta Capitals. Their trading and consulting support is very practical relatively less risky than other options available and especially I liked using the pledged investments to generate extra income without touching my core portfolio. The team is knowledgeable, responsive, and genuinely focused on helping clients grow. I'd definitely recommend them to anyone looking to improve their trading results."
                  </p>
                </div>
                <div className="border-t border-emerald-500/10 pt-4">
                  <p className="text-white font-semibold">Manjunath</p>
                  <p className="text-gray-500 text-xs">USA</p>
                </div>
              </div>

              {/* Testimonial 9 - Nikhil */}
              <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all" data-testid="testimonial-9">
                <div className="mb-4">
                  <p className="text-gray-300 text-sm leading-relaxed italic">
                    "I have been using Theta Capitals' services for over a year now, and it's been a rewarding experience. Honest communication and realistic expectations. Excellent customer support, responsive and helpful team. The platform is user-friendly and ability to trade on pledged holdings generates additional income without selling core investments. Hassle-free, plug-and-play system perfect for busy professionals. Overall, it has added a reliable source of passive income to my portfolio. Highly recommended!"
                  </p>
                </div>
                <div className="border-t border-emerald-500/10 pt-4">
                  <p className="text-white font-semibold">Nikhil</p>
                  <p className="text-gray-500 text-xs">Bengaluru</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-policy-page pt-24">
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 text-center" data-testid="privacy-policy-heading">
              Privacy <span className="text-gradient">Policy</span>
            </h1>
            <p className="text-gray-400 text-center mb-12">Last Updated: January 2025</p>

            <div className="bg-gray-800/30 border border-emerald-500/10 rounded-xl p-8 md:p-10">
              <div className="space-y-6">
                <p className="text-gray-300 text-lg leading-relaxed">
                  Theta Capitals prioritizes your privacy. We collect and use personal data to enhance user experience, process transactions, and communicate updates. Your information is securely stored and we do not share your information. We comply with applicable laws, ensuring data protection and transparency.
                </p>

                <div className="border-t border-emerald-500/10 pt-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Data Collection & Usage</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    We collect personal information that you provide to us, including but not limited to:
                  </p>
                  <ul className="space-y-2 ml-6">
                    {[
                      "Name and contact information (email, phone number)",
                      "Financial information for transaction processing",
                      "Communication preferences and inquiry details",
                      "Usage data to improve our services"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="mr-3 text-emerald-400 flex-shrink-0 mt-1" size={18} />
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-emerald-500/10 pt-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Data Security</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Your information is securely stored using industry-standard security measures. We implement appropriate technical and organizational safeguards to protect your personal data from unauthorized access, disclosure, alteration, or destruction.
                  </p>
                </div>

                <div className="border-t border-emerald-500/10 pt-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Data Sharing</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We do not share your information with third parties. Your data is used exclusively for providing our services and communicating with you directly.
                  </p>
                </div>

                <div className="border-t border-emerald-500/10 pt-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Your Rights</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Users have the right to:
                  </p>
                  <ul className="space-y-2 ml-6">
                    {[
                      "Access your personal data",
                      "Request correction of inaccurate information",
                      "Request deletion of your data",
                      "Withdraw consent for data processing",
                      "Receive a copy of your data"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle2 className="mr-3 text-emerald-400 flex-shrink-0 mt-1" size={18} />
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="border-t border-emerald-500/10 pt-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    For any privacy-related concerns or to exercise your data rights, please contact us at:
                  </p>
                  <a 
                    href="mailto:thetacapitals@gmail.com" 
                    className="inline-flex items-center space-x-2 text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                    data-testid="privacy-contact-email"
                  >
                    <Mail size={20} />
                    <span>thetacapitals@gmail.com</span>
                  </a>
                </div>

                <div className="border-t border-emerald-500/10 pt-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Legal Compliance</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We comply with all applicable data protection laws and regulations. This privacy policy may be updated periodically to reflect changes in our practices or legal requirements. We encourage you to review this page regularly.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-400 text-sm">
                Questions about our privacy practices? <Link to="/contact" className="text-emerald-400 hover:text-emerald-300">Contact us</Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Header />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/purpose" element={<Purpose />} />
          <Route path="/services" element={<Services />} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
        <Footer />
        <WhatsAppButton />
      </BrowserRouter>
    </div>
  );
}

export default App;